import {
    Component,
    computed,
    effect,
    EventEmitter,
    inject,
    Input,
    input,
    Output,
    signal,
    untracked,
} from '@angular/core';
import { MessageService } from 'primeng/api';
import { Button } from 'primeng/button';
import { Dialog } from 'primeng/dialog';
import { Message } from 'primeng/message';
import { AdminGame, RatingCounts, RatingStats } from '@/lib-backend';
import { FakeReviewService } from '@/app/services/fake-review.service';
import { ReviewDistributionForm } from './review-distribution-form';
import {
    buildBellDistribution,
    resolveGameAutoTags,
} from './review-distribution';

const COMMENTS_FETCH_CHUNK = 500;
const DEFAULT_RANGE_DAYS = 30;

/**
 * Generation form for fake reviews of a single game. Rendered as the "Generate"
 * tab inside {@link ReviewModerationDialog}; it has no dialog/toast of its own
 * and reuses the host's MessageService for notifications.
 */
@Component({
    selector: 'app-generate-reviews-panel',
    standalone: true,
    imports: [Button, Dialog, Message, ReviewDistributionForm],
    templateUrl: './generate-reviews-panel.html',
})
export class GenerateReviewsPanel {
    @Input() set adminGame(g: AdminGame | null) {
        this.gameSignal.set(g);
    }

    /** Whether the hosting tab is currently selected; gates data loading. */
    readonly active = input(false);

    /** Emitted after reviews are generated/replaced/removed, so the host can refresh. */
    @Output() changed = new EventEmitter<void>();

    private readonly fakeReviewService = inject(FakeReviewService);
    private readonly messageService = inject(MessageService);

    protected readonly gameSignal = signal<AdminGame | null>(null);

    // ── Info ────────────────────────────────────────────────────────────────
    protected readonly infoLoading = signal(false);
    protected readonly infoError = signal<string | null>(null);
    protected readonly stats = signal<RatingStats | null>(null);

    // ── Tags (auto, read-only) ────────────────────────────────────────────────
    /**
     * Tags are not operator-selectable: they are fixed to "General" plus the
     * game template's own tags. Comments for generation are drawn from this pool.
     */
    protected readonly autoTags = computed<string[]>(() =>
        resolveGameAutoTags(this.gameSignal()),
    );

    // ── Generate form ───────────────────────────────────────────────────────
    protected readonly targetAverage = signal(4.5);
    protected readonly count = signal(10);
    /** 1 = minimum spread (only floor/ceil of target), 0 = full diffusion. */
    protected readonly concentration = signal(1);
    protected readonly createdFrom = signal<Date | null>(null);
    protected readonly createdTo = signal<Date | null>(null);

    // Pool-based validation
    protected readonly commentsStat = signal<{
        totalCount: number;
        perRating: RatingCounts;
    } | null>(null);
    protected readonly activeAuthorsCount = signal<number | null>(null);

    protected readonly distribution = computed<RatingCounts>(() =>
        buildBellDistribution(
            this.targetAverage(),
            this.count(),
            this.concentration(),
        ),
    );

    // ── Busy flags ──────────────────────────────────────────────────────────
    protected readonly addBusy = signal(false);
    protected readonly replaceBusy = signal(false);
    protected readonly removeAllBusy = signal(false);
    protected readonly anyBusy = computed(
        () => this.addBusy() || this.replaceBusy() || this.removeAllBusy(),
    );

    // ── Confirms ────────────────────────────────────────────────────────────
    protected readonly replaceConfirmVisible = signal(false);
    protected readonly removeAllConfirmVisible = signal(false);

    // ── Derived ─────────────────────────────────────────────────────────────
    protected readonly totalFake = computed(() => {
        const s = this.stats();
        if (!s) return 0;
        return (
            s.Rating1.FakeCount +
            s.Rating2.FakeCount +
            s.Rating3.FakeCount +
            s.Rating4.FakeCount +
            s.Rating5.FakeCount
        );
    });

    protected readonly totalReal = computed(() => {
        const s = this.stats();
        if (!s) return 0;
        return (
            s.Rating1.RealCount +
            s.Rating2.RealCount +
            s.Rating3.RealCount +
            s.Rating4.RealCount +
            s.Rating5.RealCount
        );
    });

    protected readonly poolValidation = computed<string | null>(() => {
        const c = this.count();
        if (c <= 0) return null;
        const authors = this.activeAuthorsCount();
        if (authors !== null && c > authors) {
            return `Need ${c} active authors, only ${authors} in the pool.`;
        }
        const stat = this.commentsStat();
        if (stat) {
            const d = this.distribution();
            const buckets: [keyof RatingCounts, number][] = [
                ['Rating1', 1],
                ['Rating2', 2],
                ['Rating3', 3],
                ['Rating4', 4],
                ['Rating5', 5],
            ];
            for (const [key, star] of buckets) {
                if (d[key] > stat.perRating[key]) {
                    return `Need ${d[key]} ${star}★ comments matching tags, only ${stat.perRating[key]} available.`;
                }
            }
        }
        return null;
    });

    protected readonly generateDisabled = computed(() => {
        if (this.anyBusy()) return true;
        if (this.count() <= 0) return true;
        const t = this.targetAverage();
        if (!Number.isFinite(t) || t < 1 || t > 5) return true;
        if (this.autoTags().length === 0) return true;
        if (!this.createdFrom() || !this.createdTo()) return true;
        if (this.createdFrom()! > this.createdTo()!) return true;
        if (this.poolValidation() !== null) return true;
        return false;
    });

    protected readonly removeAllDisabled = computed(
        () => this.anyBusy() || this.totalFake() === 0,
    );

    constructor() {
        // Load (and reset) when the tab becomes active or the game changes.
        effect(() => {
            const active = this.active();
            const g = this.gameSignal();
            if (active && g) {
                untracked(() => {
                    this.resetForm();
                    this.reloadInfo();
                    this.reloadActiveAuthorsCount();
                });
            }
        });

        // Refresh per-bucket comment counts whenever the resolved tags change
        // (while the tab is active).
        effect(() => {
            const tags = this.autoTags();
            const active = this.active();
            if (!active) return;
            untracked(() => this.reloadCommentsStat(tags));
        });
    }

    private resetForm(): void {
        this.targetAverage.set(4.5);
        this.count.set(10);
        this.concentration.set(1);
        const to = new Date();
        const from = new Date();
        from.setDate(from.getDate() - DEFAULT_RANGE_DAYS);
        this.createdFrom.set(from);
        this.createdTo.set(to);
    }

    // ── Loaders ─────────────────────────────────────────────────────────────

    protected async reloadInfo(): Promise<void> {
        const g = this.gameSignal();
        if (!g) return;
        this.infoLoading.set(true);
        this.infoError.set(null);
        try {
            const outcome = await this.fakeReviewService.getGameReviewInfo(
                g.RealmId,
                g.Id,
            );
            if (outcome.result === 'Ok') {
                this.stats.set(outcome.stats);
            } else {
                this.infoError.set('Failed to load review info.');
            }
        } finally {
            this.infoLoading.set(false);
        }
    }

    private async reloadCommentsStat(tags: string[]): Promise<void> {
        if (tags.length === 0) {
            this.commentsStat.set(null);
            return;
        }
        const outcome = await this.fakeReviewService.getCommentsStat(tags);
        if (outcome.result === 'Ok') {
            this.commentsStat.set({
                totalCount: outcome.totalCount,
                perRating: outcome.perRating,
            });
        } else {
            this.commentsStat.set(null);
        }
    }

    private async reloadActiveAuthorsCount(): Promise<void> {
        let total = 0;
        let skip = 0;
        // eslint-disable-next-line no-constant-condition
        while (true) {
            const outcome = await this.fakeReviewService.listAuthors(
                true,
                skip,
                COMMENTS_FETCH_CHUNK,
            );
            if (outcome.result !== 'Ok') return;
            total += outcome.authors.length;
            if (outcome.authors.length < COMMENTS_FETCH_CHUNK) break;
            skip += COMMENTS_FETCH_CHUNK;
        }
        this.activeAuthorsCount.set(total);
    }

    // ── Actions ─────────────────────────────────────────────────────────────

    private currentRatingCounts(): RatingCounts {
        return this.distribution();
    }

    private toIso(d: Date | null): string {
        return (d ?? new Date()).toISOString();
    }

    protected async add(): Promise<void> {
        const g = this.gameSignal();
        if (!g || this.generateDisabled()) return;
        this.addBusy.set(true);
        try {
            const outcome = await this.fakeReviewService.addGameReviews({
                realmId: g.RealmId,
                gameId: g.Id,
                ratingCounts: this.currentRatingCounts(),
                tags: this.autoTags(),
                createdFrom: this.toIso(this.createdFrom()),
                createdTo: this.toIso(this.createdTo()),
            });
            this.handleGenerateOutcome('add', outcome);
        } finally {
            this.addBusy.set(false);
        }
    }

    protected requestReplace(): void {
        if (this.generateDisabled()) return;
        this.replaceConfirmVisible.set(true);
    }

    protected cancelReplace(): void {
        this.replaceConfirmVisible.set(false);
    }

    protected async confirmReplace(): Promise<void> {
        this.replaceConfirmVisible.set(false);
        await this.runReplace();
    }

    private async runReplace(): Promise<void> {
        const g = this.gameSignal();
        if (!g) return;
        this.replaceBusy.set(true);
        try {
            const outcome = await this.fakeReviewService.replaceGameReviews({
                realmId: g.RealmId,
                gameId: g.Id,
                ratingCounts: this.currentRatingCounts(),
                tags: this.autoTags(),
                createdFrom: this.toIso(this.createdFrom()),
                createdTo: this.toIso(this.createdTo()),
            });
            this.handleGenerateOutcome('replace', outcome);
        } finally {
            this.replaceBusy.set(false);
        }
    }

    private handleGenerateOutcome(
        op: 'add' | 'replace',
        outcome:
            | { result: 'Ok'; createdCount: number; stats: RatingStats }
            | {
                  result: 'Ok';
                  removedCount: number;
                  createdCount: number;
                  stats: RatingStats;
              }
            | {
                  result: 'InsufficientAuthors';
                  availableAuthors: number;
                  requestedAuthors: number;
              }
            | {
                  result: 'InsufficientComments';
                  commentsRating: number;
                  availableComments: number;
                  requestedComments: number;
              }
            | { result: 'Error' },
    ): void {
        if (outcome.result === 'Ok') {
            this.stats.set(outcome.stats);
            const removed =
                'removedCount' in outcome ? outcome.removedCount : 0;
            const detail =
                op === 'replace'
                    ? `Removed ${removed}, created ${outcome.createdCount}.`
                    : `Created ${outcome.createdCount}.`;
            this.messageService.add({
                severity: 'success',
                summary:
                    op === 'replace' ? 'Reviews replaced' : 'Reviews added',
                detail,
            });
            this.changed.emit();
            return;
        }
        if (outcome.result === 'InsufficientAuthors') {
            this.messageService.add({
                severity: 'warn',
                summary: 'Not enough authors',
                detail: `Need ${outcome.requestedAuthors}, available ${outcome.availableAuthors}. Add more fake authors first.`,
                life: 8000,
            });
            return;
        }
        if (outcome.result === 'InsufficientComments') {
            this.messageService.add({
                severity: 'warn',
                summary: 'Not enough comments',
                detail: `For ${outcome.commentsRating}★ need ${outcome.requestedComments} comments matching tags, only ${outcome.availableComments} available.`,
                life: 8000,
            });
            return;
        }
    }

    protected requestRemoveAll(): void {
        if (this.removeAllDisabled()) return;
        this.removeAllConfirmVisible.set(true);
    }

    protected cancelRemoveAll(): void {
        this.removeAllConfirmVisible.set(false);
    }

    protected async confirmRemoveAll(): Promise<void> {
        const g = this.gameSignal();
        if (!g) return;
        this.removeAllConfirmVisible.set(false);
        this.removeAllBusy.set(true);
        try {
            const outcome = await this.fakeReviewService.removeAllGameReviews(
                g.RealmId,
                g.Id,
            );
            if (outcome.result === 'Ok') {
                this.messageService.add({
                    severity: 'success',
                    summary: 'All fake reviews removed',
                    detail: `Removed ${outcome.removedCount}.`,
                });
                this.reloadInfo();
                this.changed.emit();
            }
        } finally {
            this.removeAllBusy.set(false);
        }
    }
}
